import { jsonrepair } from 'jsonrepair';

/**
 * Removes JavaScript-style line comments (//) from a JSON string.
 * This is useful when AI responses include comments in JSON output.
 *
 * @param jsonString - The JSON string potentially containing // comments
 * @returns Clean JSON string without line comments
 *
 * @example
 * ```typescript
 * const dirtyJson = `{
 *   "data": "value"
 *   // This is a comment
 * }`;
 * const clean = removeJSONComments(dirtyJson);
 * // Result: '{\n  "data": "value"\n  \n}'
 * ```
 */
export function removeJSONComments(jsonString: string): string {
  // Remove lines that start with // (with optional leading whitespace)
  // The 'm' flag makes ^ match the start of each line
  return jsonString.replace(/^\s*\/\/.*$/gm, '');
}

/**
 * Safely parses a JSON string that may contain // comments or be malformed.
 * Uses jsonrepair library to fix common JSON issues before parsing.
 *
 * @param jsonString - The JSON string to parse
 * @returns Parsed JSON object
 * @throws {SyntaxError} If the JSON cannot be repaired and parsed
 *
 * @example
 * ```typescript
 * const result = safeJSONParse(`{
 *   "name": "test"
 *   // comment here
 * }`);
 * // Returns: { name: "test" }
 * ```
 */
export function safeJSONParse<T = any>(jsonString: string): T {
  try {
    // First try to remove comments
    const cleanedJSON = removeJSONComments(jsonString);
    // Then repair any malformed JSON
    const repairedJSON = jsonrepair(cleanedJSON);
    return JSON.parse(repairedJSON) as T;
  } catch (error) {
    // If repair fails, try direct repair without comment removal
    try {
      const repairedJSON = jsonrepair(jsonString);
      return JSON.parse(repairedJSON) as T;
    } catch (repairError) {
      console.error('Failed to repair JSON:', repairError);
      throw repairError;
    }
  }
}
